import { Combobox } from "@headlessui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ChevronDown from "../Atomos/Icons/ChevronDown";
import Close from "../Atomos/Icons/Close";
import "../../assets/css/Text.css";
import "../../assets/css/InputText.css";
import { useStaffMode } from "../../hooks/StaffModeProvider";
import { ICompany } from "../../interfaces/models/ICompany";

export type CompanySelection = {
  id: string;
  name: string;
  pendingNew: boolean;
};

const emptySelection = (): CompanySelection => ({
  id: "",
  name: "",
  pendingNew: false,
});

function encodeSelection(sel: CompanySelection): string | null {
  if (sel.pendingNew && sel.name.trim()) {
    return `create:${sel.name.trim()}`;
  }
  if (sel.id && sel.name.trim()) {
    return `existing:${sel.id}`;
  }
  return null;
}

function decodeDisplay(
  value: string | null | undefined,
  companies: ICompany[]
): string {
  if (!value) {
    return "";
  }
  if (value.startsWith("create:")) {
    return value.slice("create:".length);
  }
  if (value.startsWith("existing:")) {
    const id = value.slice("existing:".length);
    return companies.find((c) => c.id === id)?.name ?? "";
  }
  return "";
}

type Props = {
  companies: ICompany[];
  selection: CompanySelection;
  onSelectionChange: (next: CompanySelection) => void;
  title?: string;
  required?: boolean;
  error?: boolean;
  errormsj?: string;
  disabled?: boolean;
};

const CompanyCreatableCombobox: React.FC<Props> = ({
  companies,
  selection,
  onSelectionChange,
  title = "Company",
  required = false,
  error = false,
  errormsj = "",
  disabled = false,
}) => {
  const { isStaffMode } = useStaffMode();
  const color = isStaffMode ? "secondary" : "primary";
  const [query, setQuery] = useState(selection.name || "");

  const comboboxValue = encodeSelection(selection);

  useEffect(() => {
    if (selection.id || selection.pendingNew) {
      setQuery(selection.name || "");
    }
  }, [selection.id, selection.pendingNew, selection.name]);

  const sortedCompanies = useMemo(() => {
    return [...companies].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      })
    );
  }, [companies]);

  const filteredCompanies = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return sortedCompanies;
    }
    return sortedCompanies.filter((c) =>
      (c.name || "").toLowerCase().includes(q)
    );
  }, [sortedCompanies, query]);

  const hasExactNameMatch = useMemo(() => {
    const t = query.trim().toLowerCase();
    if (!t) {
      return false;
    }
    return companies.some(
      (c) => (c.name || "").trim().toLowerCase() === t
    );
  }, [companies, query]);

  const showCreateOption =
    query.trim().length > 0 && !hasExactNameMatch;

  const isNewCompanyPending =
    selection.pendingNew && selection.name.trim().length > 0;

  const skipBlurCommit = useRef(false);

  const commitQueryAsSelection = () => {
    if (disabled) {
      return;
    }
    const trimmed = query.trim();
    if (!trimmed) {
      if (selection.id || selection.pendingNew) {
        setQuery("");
        onSelectionChange(emptySelection());
      }
      return;
    }

    const exactMatch = companies.find(
      (c) => (c.name || "").trim().toLowerCase() === trimmed.toLowerCase()
    );

    if (exactMatch) {
      if (selection.id !== exactMatch.id || selection.pendingNew) {
        setQuery(exactMatch.name);
        onSelectionChange({
          id: exactMatch.id,
          name: exactMatch.name,
          pendingNew: false,
        });
      }
      return;
    }

    if (!selection.pendingNew || selection.name !== trimmed) {
      setQuery(trimmed);
      onSelectionChange({ id: "", name: trimmed, pendingNew: true });
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) {
      return;
    }
    setQuery("");
    onSelectionChange(emptySelection());
  };

  const handleComboboxChange = (val: string | null) => {
    if (!val) {
      setQuery("");
      onSelectionChange(emptySelection());
      return;
    }
    if (val.startsWith("create:")) {
      const name = val.slice("create:".length);
      setQuery(name);
      onSelectionChange({ id: "", name, pendingNew: true });
      return;
    }
    if (val.startsWith("existing:")) {
      const id = val.slice("existing:".length);
      const c = companies.find((x) => x.id === id);
      if (c) {
        setQuery(c.name);
        onSelectionChange({
          id: c.id,
          name: c.name,
          pendingNew: false,
        });
      }
    }
  };

  return (
    <div
      className={`flex flex-col gap-2 md:gap-[.625rem] h-max w-full ${
        error ? "bottom-0 relative" : ""
      }`}
    >
      {title && (
        <div className="flex gap-2 w-full">
          <label className="text-base md:text-lg font-medium text-gray-font">
            {title} {required && <span className="text-primary"> *</span>}
          </label>
        </div>
      )}

      <Combobox
        value={comboboxValue}
        onChange={handleComboboxChange}
        disabled={disabled}
      >
        {({ open }) => {
          return (
            <div className="relative">
              <div
                className={`relative rounded-sm ${
                  error
                    ? "errorInput"
                    : isNewCompanyPending
                    ? "outline outline-2 outline-feedback-success-Default [&_input]:!border-feedback-success-Default [&_input]:!bg-green-50"
                    : ""
                }`}
              >
                <Combobox.Button className="w-full">
                  <div className="flex flex-row relative">
                    <Combobox.Input
                      autoComplete="new-password"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      onBlur={() => {
                        if (skipBlurCommit.current) {
                          skipBlurCommit.current = false;
                          return;
                        }
                        commitQueryAsSelection();
                      }}
                      className={`${
                        error ? "text-primary" : "text-input-gray"
                      } cursor-pointer`}
                      style={
                        comboboxValue
                          ? {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign: "left",
                              direction: "ltr",
                            }
                          : undefined
                      }
                      displayValue={(v: string | null) =>
                        open ? query : decodeDisplay(v, companies)
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        setQuery(v);
                        onSelectionChange(emptySelection());
                      }}
                      name={title}
                    />
                    {comboboxValue && (
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 right-10 h-4 w-4 md:h-5 md:w-5 cursor-pointer z-1 ${
                          disabled
                            ? "fill-gray-300 pointer-events-none"
                            : "fill-gray-font"
                        }`}
                        onClick={handleClear}
                      >
                        <Close />
                      </div>
                    )}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <div
                        className={`${
                          disabled ? "text-gray-200" : "text-gray-font"
                        } w-5 h-5 md:w-6 md:h-6 ${
                          open && "rotate-180 duration-75"
                        } ${disabled ? "fill-gray-300" : "fill-gray-font"}`}
                      >
                        <ChevronDown />
                      </div>
                    </div>
                  </div>
                </Combobox.Button>
              </div>

              <Combobox.Options
                className="absolute top-14 w-full max-h-48 z-30 bg-white rounded-sm shadow-2xl overflow-y-auto"
                onMouseDown={() => {
                  skipBlurCommit.current = true;
                }}
              >
                {filteredCompanies.map((c) => (
                  <Combobox.Option
                    key={c.id}
                    value={`existing:${c.id}`}
                    className={`px-2 md:px-4 py-2 text-base md:text-lg leading-[19px] text-left cursor-pointer ${
                      selection.id === c.id
                        ? `bg-${color} !text-white`
                        : "bg-white hover:bg-gray-50"
                    }`}
                  >
                    {c.name}
                  </Combobox.Option>
                ))}
                {showCreateOption && (
                  <Combobox.Option
                    key={`create-${query.trim()}`}
                    value={`create:${query.trim()}`}
                    className="px-2 md:px-4 py-2 text-base md:text-lg leading-[19px] text-left cursor-pointer bg-white hover:bg-gray-50 text-primary font-medium border-t border-gray-100"
                  >
                    Create company &quot;{query.trim()}&quot;
                  </Combobox.Option>
                )}
                {filteredCompanies.length === 0 && !showCreateOption && (
                  <div className="px-2 md:px-4 py-2 text-base md:text-lg text-gray-500 text-center">
                    No results found
                  </div>
                )}
              </Combobox.Options>
            </div>
          );
        }}
      </Combobox>
      {isNewCompanyPending && !error && (
        <span className="flex flex-wrap items-center text-base font-medium text-feedback-success-Default">
          A new Company with this name will be created
        </span>
      )}
      {error && errormsj && (
        <span className="flex flex-wrap items-center text-base font-medium text-primary">
          {errormsj}
        </span>
      )}
    </div>
  );
};

export default CompanyCreatableCombobox;
